/**
 * Unified Payment Status Manager
 * 
 * After ANY successful payment, user gets FULL access to:
 * - MCQs (levels 6+)
 * - Challenges (Advanced & Expert)
 * - LLM (unlimited, no trial restriction)
 * - Mock Interviews (all buttons, unlimited clicks)
 * - All 5 dashboards
 */

import API_BASE_URL from '../Config/config';

// Payment status checks database + localStorage
export const checkPaymentStatus = async (userId) => {
  if (!userId) {
    return {
      hasPayment: false,
      isValid: false,
      source: 'none'
    };
  }

  try {
    // Check database for active payment/subscription
    const encodedUserId = encodeURIComponent(userId);
    const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/status/${encodedUserId}`, {
      signal: AbortSignal.timeout(5000)
    });

    if (response.ok) {
      const data = await response.json();
      console.log(`[Payment Check] Database response for ${userId}:`, {
        ok: data.ok,
        hasActiveSubscription: data.hasActiveSubscription,
        subscription: data.subscription
      });
      
      if (data.ok && data.hasActiveSubscription && data.subscription) {
        // Database shows active subscription - validate it's not expired
        const subscription = data.subscription;
        if (subscription.end_date) {
          const endDate = new Date(subscription.end_date);
          const now = new Date();
          if (endDate <= now) {
            // Subscription expired - return no payment
            console.warn(`[Payment Check] Subscription expired for ${userId}. End date: ${subscription.end_date}, Now: ${now.toISOString()}`);
            return {
              hasPayment: false,
              isValid: false,
              source: 'database-expired'
            };
          }
        }
        
        // Valid active subscription
        console.log(`[Payment Check] Valid subscription found for ${userId}. Days remaining: ${subscription.daysRemaining}`);
        return {
          hasPayment: true,
          isValid: true,
          daysLeft: subscription.daysRemaining,
          expiryDate: subscription.end_date,
          paymentMethod: subscription.payment_method,
          planName: subscription.plan_name,
          source: 'database'
        };
      } else {
        // Database explicitly says no active subscription - don't check localStorage
        console.log(`[Payment Check] No active subscription found in database for ${userId}`);
        // Clear any stale localStorage payment data
        const paymentKey = `payment_confirmed_${userId}`;
        const storedPayment = localStorage.getItem(paymentKey);
        if (storedPayment) {
          console.warn('⚠️ Database shows no subscription but localStorage has payment data - clearing localStorage');
          localStorage.removeItem(paymentKey);
        }
        return {
          hasPayment: false,
          isValid: false,
          source: 'database'
        };
      }
    } else {
      console.warn(`[Payment Check] API returned error status ${response.status} for ${userId}`);
    }
  } catch (error) {
    console.warn('⚠️ Payment status API unavailable - checking localStorage:', error.message);
  }

  // Fallback: Check localStorage for payment confirmation
  // NOTE: Only trust localStorage if database check failed (API unavailable)
  // If database check succeeded but returned no subscription, don't use localStorage
  const paymentKey = `payment_confirmed_${userId}`;
  const storedPayment = localStorage.getItem(paymentKey);

  if (storedPayment) {
    try {
      const payment = JSON.parse(storedPayment);
      
      // Always validate expiry date - if expired, clear it
      if (payment.expiryDate) {
        const expiryDate = new Date(payment.expiryDate);
        const now = new Date();
        const isValid = now < expiryDate;
        
        if (!isValid) {
          // Payment expired - clear localStorage
          console.warn('⚠️ Stored payment expired, clearing localStorage');
          localStorage.removeItem(paymentKey);
          return {
            hasPayment: false,
            isValid: false,
            source: 'none'
          };
        }
        
        return {
          hasPayment: true,
          isValid: true,
          daysLeft: Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)),
          expiryDate: payment.expiryDate,
          paymentMethod: payment.paymentMethod,
          planName: payment.planName,
          source: 'localStorage'
        };
      } else {
        // No expiry date - this shouldn't happen, but if it does, don't trust it
        // Only allow if it's a very recent confirmation (within last hour)
        const confirmedAt = payment.confirmedAt ? new Date(payment.confirmedAt) : null;
        const now = new Date();
        const isRecent = confirmedAt && (now - confirmedAt) < 3600000; // 1 hour
        
        if (!isRecent) {
          console.warn('⚠️ Stored payment has no expiry and is not recent, clearing localStorage');
          localStorage.removeItem(paymentKey);
          return {
            hasPayment: false,
            isValid: false,
            source: 'none'
          };
        }
        
        return {
          hasPayment: true,
          isValid: true,
          daysLeft: null,
          expiryDate: null,
          paymentMethod: payment.paymentMethod,
          planName: payment.planName,
          source: 'localStorage'
        };
      }
    } catch (error) {
      console.error('Error parsing payment data:', error);
      // If payment data is corrupted, clear it
      localStorage.removeItem(paymentKey);
    }
  }

  // No payment found
  return {
    hasPayment: false,
    isValid: false,
    source: 'none'
  };
};

/**
 * Mark payment as confirmed
 * Called after successful Razorpay verification
 */
export const confirmPayment = (userId, paymentData) => {
  const {
    planName,
    amount,
    expiryDate,
    paymentMethod = 'razorpay',
    orderId,
    paymentId
  } = paymentData;

  const confirmation = {
    userId,
    planName,
    amount,
    expiryDate,
    paymentMethod,
    orderId,
    paymentId,
    confirmedAt: new Date().toISOString(),
    status: 'confirmed'
  };

  localStorage.setItem(`payment_confirmed_${userId}`, JSON.stringify(confirmation));

  // Also unlock all mock interview clicks
  try {
    const { unlockAllClicks } = require('./mockInterviewClickTracker');
    unlockAllClicks(userId);
  } catch (err) {
    console.warn('Could not unlock mock interview clicks:', err.message);
  }

  return confirmation;
};

/**
 * IMPORTANT: After payment, user gets access to ALL features
 * This should be checked before showing any paywall
 */
export const hasFullAccess = async (userId) => {
  // Bypass for test credentials
  const user = JSON.parse(localStorage.getItem("user"));
  if (user && (user.email === 'test@example.com' || user.username === 'testuser' || user.role === 'admin')) {
    return true;
  }

  // ✅ NEW: Student Tenant Access
  // As it is student tenant login, provide entire application access.
  // We identify them by role 'student' AND presence of a tenant slug.
  // We also check if we are on a tenant subdomain.
  const isMainPlatform = window.location.hostname === 'theskillgenie.com' || 
                         window.location.hostname === 'www.theskillgenie.com' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

    if (user && (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || !isMainPlatform)) {
    console.log(`[Access Control] Full access granted to Student Tenant: ${userId}`);
    return true;
  }
  
  const paymentStatus = await checkPaymentStatus(userId);
  return paymentStatus.hasPayment && paymentStatus.isValid;
};

/**
 * Redirect to payment if no access
 */
export const redirectToPaymentIfNeeded = async (userId, navigate) => {
  const hasAccess = await hasFullAccess(userId);
  if (!hasAccess) {
    navigate('/skillgenpayments');
    return false;
  }
  return true;
};

/**
 * Clear payment data (for logout or account changes)
 */
export const clearPaymentStatus = (userId) => {
  const paymentKey = `payment_confirmed_${userId}`;
  localStorage.removeItem(paymentKey);
};

/**
 * Clear all stale payment data (for debugging/fixing issues)
 */
export const clearAllStalePaymentData = () => {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('payment_confirmed_') || key.startsWith('tsg_used_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data.expiryDate) {
          const expiryDate = new Date(data.expiryDate);
          if (expiryDate <= new Date()) {
            console.log(`Clearing expired payment data: ${key}`);
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        // If parsing fails, it might be corrupted - clear it
        console.log(`Clearing corrupted payment data: ${key}`);
        localStorage.removeItem(key);
      }
    }
  });
};

export default {
  checkPaymentStatus,
  confirmPayment,
  hasFullAccess,
  redirectToPaymentIfNeeded,
  clearPaymentStatus
};










